import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import PlaidLinkWithRetry from '@/components/plaid-link-with-retry'
import { registerPlaidLinkHandlers } from '@/lib/plaid-link-bus'

// Singleton Plaid Link host mounted once in PrivateLayout.
// Usage from anywhere:
//   import { plaidPrepare, plaidOpen, isPlaidReady } from '@/lib/plaid-link-bus'
//   plaidPrepare()  // lazily create link token
//   plaidOpen()     // waits for ready then opens
//   isPlaidReady()  // check if Plaid is ready
const PlaidLinkSingleton = () => {
  const [prepared, setPrepared] = useState(false)
  const openRef = useRef(() => {})
  const readyRef = useRef(false)

  const waitForReady = async (timeoutMs = 10000) => {
    const start = Date.now()
    while (!readyRef.current) {
      if (Date.now() - start > timeoutMs) throw new Error('Plaid Link not ready')
      await new Promise(r => setTimeout(r, 50))
    }
  }

  useEffect(() => {
    registerPlaidLinkHandlers({
      prepare: () => {
        setPrepared(true)
      },
      open: async () => {
        if (!prepared) setPrepared(true)
        await waitForReady()
        openRef.current?.()
      },
      getReady: () => readyRef.current,
    })
    return () => {
      registerPlaidLinkHandlers({})
    }
  }, [prepared])

  if (!prepared) return null

  return createPortal(
    <PlaidLinkWithRetry>
      {({ open, ready }) => {
        openRef.current = open
        readyRef.current = !!ready
        return (
          <div style={{ display: 'none' }}>
            <button onClick={open} disabled={!ready} />
          </div>
        )
      }}
    </PlaidLinkWithRetry>,
    document.body
  )
}

export default PlaidLinkSingleton
